import { ModalTypes } from 'enums/modalTypes';
import {
  CognitiveMode,
  CognitiveTestActionFragment,
  ProjectPageGetProjectByIdDocument,
  useRemoveCognitiveTestFromAssessmentStageMutation,
  useUpdateCognitiveTestSectionCountMutation,
} from 'generated/graphql';
import { closeModal, openModal } from 'graphql/modals';
import { FC, useState } from 'react';
import { NotificationModalProps } from 'views/ModalView/NotificationModal';
import {
  Button,
  Modal,
  NotificationModalType,
  TextInputField,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import {
  cognitiveTestLabel,
  MAX_TEST_QUESTIONS,
  MIN_TEST_QUESTIONS,
} from '../helpers';

interface IEditCognitiveTestActionModal {
  action: CognitiveTestActionFragment;
  stageCognitiveMode: CognitiveMode;
  projectId: number;
  onClose: () => void;
}

const EditCognitiveTestActionModal: FC<IEditCognitiveTestActionModal> = ({
  action,
  stageCognitiveMode,
  projectId,
  onClose,
}) => {
  const { handleMsgType } = useNotification();
  const [submitting, setSubmitting] = useState(false);
  const [sectionCount, setSectionCount] = useState(
    action.sectionCount.toString()
  );

  const testLabel = cognitiveTestLabel(action.kind);
  const minSections = MIN_TEST_QUESTIONS[stageCognitiveMode][action.kind];
  const maxSections = MAX_TEST_QUESTIONS[stageCognitiveMode][action.kind];

  const parsedSectionCount = Number(sectionCount);
  const isSectionCountValid =
    Number.isInteger(parsedSectionCount) &&
    parsedSectionCount >= minSections &&
    parsedSectionCount <= maxSections;

  const refetchQueries = [
    { query: ProjectPageGetProjectByIdDocument, variables: { id: projectId } },
  ];

  const [updateSectionCount] = useUpdateCognitiveTestSectionCountMutation({
    refetchQueries,
  });

  const [removeCognitiveTest] = useRemoveCognitiveTestFromAssessmentStageMutation(
    {
      refetchQueries,
      onError: error => {
        handleMsgType({ type: TNotification.error, message: error?.message });
        setSubmitting(false);
      },
      onCompleted: () => {
        handleMsgType({
          type: TNotification.success,
          message: `${testLabel} removed!`,
        });
        onClose();
      },
    }
  );

  const handleRemove = () => {
    const confirmationModalProps: NotificationModalProps = {
      message: `This action will remove the "${testLabel}" from the stage`,
      okButtonChildren: 'OK',
      type: NotificationModalType.WARNING,
      onOkClick: () => {
        setSubmitting(true);
        closeModal(ModalTypes.NOTIFICATION_MODAL);
        removeCognitiveTest({ variables: { id: action.id } });
      },
    };
    openModal(ModalTypes.NOTIFICATION_MODAL, confirmationModalProps);
  };

  const hasChanges = sectionCount !== action.sectionCount.toString();

  const handleSave = async () => {
    if (!hasChanges) {
      onClose();
      return;
    }

    setSubmitting(true);
    try {
      await updateSectionCount({
        variables: { id: action.id, sectionCount: parsedSectionCount },
      });
      handleMsgType({
        type: TNotification.success,
        message: 'Cognitive test updated!',
      });
      onClose();
    } catch (error) {
      handleMsgType({
        type: TNotification.error,
        message:
          error instanceof Error
            ? error.message
            : 'Could not update the cognitive test.',
      });
      setSubmitting(false);
    }
  };

  return (
    <Modal
      onClose={onClose}
      isOpen
      className="edit-test-action-modal"
      header={`${testLabel} configuration`}
    >
      <div className="edit-test-action-modal__content">
        <TextInputField
          id={`cognitive-test-action-${action.id}-section-count`}
          label="Number of testlets"
          placeholder="Number of testlets"
          value={sectionCount}
          onChange={setSectionCount}
        />
        {!isSectionCountValid && (
          <div className="edit-test-action-modal__error">
            {`The number of testlets must be between ${minSections} and ${maxSections}.`}
          </div>
        )}
      </div>
      <div className="edit-test-action-modal__actions">
        <Button
          onClick={handleRemove}
          size="medium"
          variant="negativeSecondary"
          disabled={submitting}
          className="edit-test-action-modal__remove-button"
          data-testid="edit-cognitive-test-action-modal__remove-button"
        >
          Remove
        </Button>
        <Button onClick={onClose} size="medium" variant="secondary">
          Cancel
        </Button>
        <Button
          size="medium"
          disabled={submitting || !isSectionCountValid}
          onClick={() => handleSave()}
          data-testid="edit-cognitive-test-action-modal__save-button"
        >
          Save
        </Button>
      </div>
    </Modal>
  );
};

export default EditCognitiveTestActionModal;
